import { useState } from "react";
import {
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Card,
} from "@mui/material";
import { lighten, darken } from "@mui/system";
import DeleteIcon from "@mui/icons-material/Delete";
import useDeleteData from "@/hooks/deleteHooks/useDeleteData";
import usePostJsonData from "@/hooks/postHooks/usePostJsonData";
import { useTheme } from "@mui/material/styles";
import SystemSecurityUpdateWarningIcon from "@mui/icons-material/SystemSecurityUpdateWarning";
import NewDeleteModal from "@components/newDeleteModal";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";
import { useQueryClient } from "@tanstack/react-query"; // For cache management

function PriorityLevels() {
  const theme = useTheme();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();
  const queryClient = useQueryClient(); // Initialize useQueryClient for cache management

  const [newPriority, setNewPriority] = useState("");
  const { mutate: deletePriority } = useDeleteData(
    "/api/v1/app-admin/observation-priorities",
    ["observationPriorities"]
  );

  const { mutate: postJsonData, isLoading: postIsLoading } = usePostJsonData(); // Updated hook

  const {
    data: priorities,
    isLoading: prioritiesLoading,
    error: errorPriorities,
  } = useAuthenticatedQuery(
    ["observationPriorities"],
    "/api/v1/metadata/observation-priorities"
  );

  const [itemToDelete, setItemToDelete] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isDarkMode = theme.palette.mode === "dark";
  const borderColor = isDarkMode
    ? lighten(theme.palette.divider, 0.2)
    : darken(theme.palette.divider, 0.2);

  if (prioritiesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorPriorities) {
    return (
      <Typography variant="body1" color="error">
        An error occurred. Please check your network connection and refresh the
        page.
      </Typography>
    );
  }

  const addPriority = (newPriority, endpoint) => {
    if (newPriority.trim() === "") return;

    const formattedData = { names: [newPriority] };

    postJsonData(
      { url: endpoint, data: formattedData },
      {
        onSuccess: () => {
          setNewPriority("");

          setToast({
            open: true,
            message: "Priority added successfully.",
            severity: "success",
          });
          queryClient.invalidateQueries(["observationPriorities"]); // Invalidate cache to refetch data
        },
        onError: (err) => {
          handleApiError(err);
        },
      }
    );
  };

  const confirmDelete = (id) => {
    setItemToDelete(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deletePriority(itemToDelete);
      setToast({
        open: true,
        message: "priority deleted successfully.",
        severity: "success",
      });
    } catch (err) {
      handleApiError(err);
    } finally {
      setItemToDelete(null);
      setConfirmOpen(false);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: "background.default" }}>
      <Paper
        elevation={3}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          p: 2,
          mb: 4,

          display: "flex",
          alignItems: "center",
          gap: 2,
          backgroundColor: isDarkMode
            ? darken(theme.palette.background.paper, 0.1)
            : lighten(theme.palette.background.paper, 0.1),
        }}
      >
        <TextField
          label="New Priority Level"
          size="small"
          variant="outlined"
          value={newPriority}
          onChange={(e) => setNewPriority(e.target.value)}
          fullWidth
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            addPriority(newPriority, "/api/v1/org-admin/observation-priorities")
          }
          disabled={postIsLoading || newPriority.trim() === ""}
          startIcon={
            postIsLoading ? (
              <CircularProgress size={20} />
            ) : (
              <SystemSecurityUpdateWarningIcon fontSize="small" />
            )
          }
        >
          Add
        </Button>
      </Paper>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Priority List
        </Typography>
        {priorities.length > 0 ? (
          <List>
            {priorities.map((type) => (
              <Card
                key={type.id}
                sx={{
                  mb: 2,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                }}
              >
                <ListItem
                  sx={{
                    "&:hover": {
                      backgroundColor: isDarkMode
                        ? lighten(theme.palette.background.default, 0.2)
                        : darken(theme.palette.background.default, 0.2),
                    },
                    borderBottom: `2px solid ${borderColor}`,
                  }}
                  secondaryAction={
                    <IconButton
                      color="error"
                      onClick={() => confirmDelete(type.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "primary.light" }}>
                      <SystemSecurityUpdateWarningIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={type.name}
                    sx={{
                      "& .MuiListItemText-primary": { fontWeight: "bold" },
                    }}
                  />
                </ListItem>
              </Card>
            ))}
          </List>
        ) : (
          <Typography variant="body1" align="center">
            No Priority available. Please add a new priority.
          </Typography>
        )}
      </Paper>

      <NewDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        itemType="Priority"
      />

      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </Box>
  );
}

export default PriorityLevels;
